import { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Box, Divider, Tab, Tabs } from '@mui/material'
import usePermission from '@/hooks/usePermission'
import useIsSuperUser from '@/services/hooks/useIsSuperUser'
import { SUPER_USER_ONLY_COLOR } from '@/theme/usePixydocsTheme'
import { useDemoModeContext } from '@/components/demo-mode-provider/DemoModeProvider'
import TabPanel from '@/components/TabPanel'
import { useRootOrganization } from '@/components/organizations/RootOrganizationProvider'
import CustomVariables from './CustomVariables'
import CustomVariableValues from './CustomVariableValues'
import ValuesAcrossOrgs from './ValuesAcrossOrgs'

const tabsMap: Record<string, number> = {
  variables: 0,
  values: 1,
  values_across_orgs: 2,
}

export default function CustomVariablesDetails() {
  const { '*': id } = useParams()
  const navigate = useNavigate()
  const isSuperUser = useIsSuperUser()

  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedTab, setSelectedTab] = useState(
    tabsMap[searchParams.get('tab') || ''] ?? 1,
  )
  const { rootOrganization } = useRootOrganization()
  const [demoMode] = useDemoModeContext()
  const { hasEditingPermission } = usePermission()

  const canEdit = hasEditingPermission('edit_orgs', rootOrganization.id)

  const handleChange = (
    event: React.SyntheticEvent<Element, Event>,
    newValue: number,
  ) => {
    setSelectedTab(newValue)
    setSearchParams(
      (prev) => {
        prev.set('tab', Object.keys(tabsMap)[newValue])
        return prev
      },
      { replace: true },
    )
  }

  useEffect(() => {
    const defaultTab = Object.keys(tabsMap)[1]
    const currentTab = searchParams.get('tab')
    if (!id) {
      navigate(`${rootOrganization.id}?tab=${currentTab ?? defaultTab}`, {
        replace: true,
      })
    } else if (!currentTab) {
      setSearchParams(
        (prev) => {
          prev.append('tab', defaultTab)
          return prev
        },
        { replace: true },
      )
      setSelectedTab(1)
    }
  }, [id, navigate, rootOrganization.id, searchParams, setSearchParams])

  return (
    <Box>
      <Tabs value={selectedTab} onChange={handleChange}>
        {canEdit && <Tab label="Variables" value={tabsMap.variables} />}
        <Tab label="Values" value={tabsMap.values} />
        {!demoMode && isSuperUser && (
          <Tab
            label="Values Across Orgs"
            sx={{ color: SUPER_USER_ONLY_COLOR }}
            value={tabsMap.values_across_orgs}
          />
        )}
      </Tabs>
      <Divider />
      {canEdit && (
        <TabPanel value={selectedTab} index={tabsMap.variables}>
          <CustomVariables />
        </TabPanel>
      )}
      <TabPanel value={selectedTab} index={tabsMap.values}>
        <CustomVariableValues />
      </TabPanel>
      {!demoMode && isSuperUser && (
        <TabPanel value={selectedTab} index={tabsMap.values_across_orgs}>
          <ValuesAcrossOrgs />
        </TabPanel>
      )}
    </Box>
  )
}
